import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useOutletContext } from "react-router-dom";
import { BackArrowIcon } from "../../assets/icons";
import Loader from "../../components/Loader";
import img from "../../assets/images/noProfile.png";
import { useSidebar } from "../../content/SidebarContent";
import {
  reqtoGetProfile,
  reqtoToggleTwoFA,
  reqtoTwoFASetup,
  reqtoTwoFAVerify,
  reqToUpdateProfile,
} from "../../redux/slices.js/authentication/authenticationSlice";
import Modal from "../../components/Modal";
// import { reqToUpdateProfile } from "../../redux/slices.js/admins/adminsSlice";

const validationSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email is Required!"),
  name: Yup.string().required("Name is Required!"),
  phone: Yup.string().nullable(),
});

const Profile = () => {
  const dispatch = useDispatch();
  const { setIsSidebarOpen } = useSidebar();

  // Redux state
  const { value, status, setupData, setupStatus } =
    useSelector((state) => state.authentication) || {};

  // Local state
  const [isTwoFaEnabled, setIsTwoFaEnabled] = useState(false);

  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showBackupModal, setShowBackupModal] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [token, setToken] = useState("");

  const [enableLoading, setEnableLoading] = useState(false);
  const [disableLoading, setDisableLoading] = useState(false);
  const [profileFile, setProfileFile] = useState(null);
  const { isSidebarOpen } = useOutletContext();
  const navigate = useNavigate();

  const user = useSelector((state) => state.admins.user);
  const loader = useSelector((state) => state.admins.loader);

  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    dispatch(reqtoGetProfile());
  }, [dispatch]);

  useEffect(() => {
    if (status === "succeeded" && value?.data?.twofa_enabled !== undefined) {
      setIsTwoFaEnabled(value.data.twofa_enabled);
    }
  }, [status, value]);

  const initialValues = {
    email: user?.email || "",
    name: user?.name || "",
    phone: user?.phone || "",
    profile: user?.profile || "",
  };

  // const handleSubmit = (values) => {
  //   console.log("values is here :::>>><<<", values);
  //   dispatch(
  //     reqToUpdateProfile({
  //       data: { ...values, action: "set-info" },
  //       onSuccess: () => setIsEditing(false),
  //       onFailure: () => {},
  //     })
  //   );
  //   setIsEditing(false);
  // };

  const handleSubmit = (values) => {
    const formData = new FormData();
    formData.append("name", values.name);
    formData.append("email", values.email); // Add email field
    formData.append("phone", values.phone || "");
    // formData.append("action", "set-info");

    // Add profile file if selected
    if (profileFile) {
      formData.append("profile", profileFile);
    }

    dispatch(
      reqToUpdateProfile({
        data: formData,
        onSuccess: () => {
          setIsEditing(false);
          setProfileFile(null); // Reset profile file state
          dispatch(reqtoGetProfile());
        },
        onFailure: (err) => {
          console.error("Profile update failed:", err);
        },
      })
    );
  };

  const handleEnable = async () => {
    setEnableLoading(true);
    try {
      await dispatch(reqtoTwoFASetup());
      setShowSetupModal(true);
    } catch (err) {
      console.error("2FA setup failed:", err);
    } finally {
      setEnableLoading(false);
    }
  };

  /** ---------------- VERIFY OTP ---------------- */
  const handleOtp = async () => {
    if (token.length !== 6) {
      alert("Please enter a valid 6-digit OTP.");
      return;
    }
    try {
      const result = await dispatch(reqtoTwoFAVerify({ token })).unwrap();
      if (result?.status) {
        setIsTwoFaEnabled(true);
        setShowSetupModal(false);
        setToken("");
        setShowBackupModal(true);
        dispatch(reqtoGetProfile());
      } else {
        alert("Invalid OTP, please try again.");
      }
    } catch (err) {
      console.error("OTP verification failed:", err);
      alert("Verification failed. Please try again.");
    }
  };

  /** ---------------- DISABLE MFA ---------------- */
  const handleConfirmDisable = async () => {
    setDisableLoading(true);
    setShowModal(false);
    try {
      const result = await dispatch(reqtoToggleTwoFA()).unwrap();
      setIsTwoFaEnabled(result.data.twofa_enabled);
      dispatch(reqtoGetProfile());
    } catch (err) {
      console.error("Disable 2FA failed:", err);
    } finally {
      setDisableLoading(false);
    }
  };

  return (
    <>
      {loader && <Loader />}
      <div className="rounded-md border-[1px] border-solid border-[#F3EFEF] sm:pb-6 h-full">
        <div className="p-3 sm:p-6 bg-[#F5FFBD] space-y-6">
          <div className="mb-4">
            <button
              type="button"
              className="text-[16px] font-normal flex gap-1"
              onClick={() => navigate(-1)}
            >
              {BackArrowIcon} Back
            </button>
          </div>
          <div className="heading flex gap-2 justify-between items-end">
            <div className="heading-title space-y-1">
              <h1 className="text-h2 !font-medium mb-0 text-[#4285F4]">
                Profile Management
              </h1>
              <p className="text-span text-dark mb-0">
                Manage and update your profile information
              </p>
            </div>
          </div>
        </div>
        <div className="p-3 pt-6 sm:p-6 comman-form h-[calc(100%-185px)] flex flex-col overflow-y-auto scroll-none">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, isSubmitting }) => (
              <Form className="comman-form flex flex-col">
                <div className="bg-white space-y-7">
                  <h2 className="text-h4 text-blue flex items-center justify-end">
                    {!isEditing && (
                      <button
                        type="button"
                        onClick={() => setIsEditing(true)}
                        className="next-btn"
                      >
                        Edit Profile
                      </button>
                    )}
                  </h2>

                  <div className="flex items-center gap-4 w-1/2 w-full">
                    <img
                      src={values.profile || img}
                      alt="Profile"
                      className="w-20 h-20 rounded-full object-cover border"
                    />
                    {isEditing && (
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setProfileFile(file); // Set the file for FormData
                            setFieldValue("profile", URL.createObjectURL(file)); // Set preview URL
                          }
                        }}
                      />
                    )}
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="form-group">
                      <label>Email</label>
                      <Field
                        name="email"
                        type="email"
                        className="w-full"
                        disabled={!isEditing}
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <div className="form-group">
                      <label>Name</label>
                      <Field
                        name="name"
                        className="w-full"
                        disabled={!isEditing}
                      />
                      <ErrorMessage
                        name="name"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        name="phone"
                        placeholder="Enter phone number"
                        className="w-full"
                        disabled={!isEditing}
                        value={values.phone || ""}
                        onChange={(e) => {
                          // Allow only numbers and limit to 10 digits
                          const value = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 10);
                          setFieldValue("phone", value);
                        }}
                      />
                      <ErrorMessage
                        name="phone"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>
                  </div>
                </div>

                {isEditing && (
                  <div className="mt-auto flex gap-3 pt-6 justify-end flex-wrap">
                    <button
                      type="button"
                      className="previous-btn"
                      onClick={() => setIsEditing(false)}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="next-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                )}
              </Form>
            )}
          </Formik>

          <div className="space-y-4 mt-6">
            <div className="form-group">
              <label>Two Factor Authentication</label>
            </div>
            <div className="flex items-center gap-3">
              <label className="">Status:</label>
              {isTwoFaEnabled ? (
                <button
                  onClick={() => setShowModal(true)}
                  disabled={disableLoading}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors
                          bg-red-500 text-white hover:bg-red-600
                          ${
                            disableLoading
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }
                        `}
                >
                  {disableLoading ? "Disabling..." : "Disable"}
                </button>
              ) : (
                <button
                  onClick={handleEnable}
                  disabled={enableLoading}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors
                          bg-green-500 text-white hover:bg-green-600
                          ${
                            enableLoading ? "opacity-50 cursor-not-allowed" : ""
                          }
                        `}
                >
                  {enableLoading ? "Enabling..." : "Enable"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {showSetupModal && (
        <Modal
          show={showSetupModal}
          onClose={() => {
            setShowSetupModal(false);
            setToken("");
          }}
          onConfirm={handleOtp}
          title="Setup Two-Factor Authentication"
        >
          {setupStatus === "loading" ? (
            <p className="text-center text-gray-600">Loading setup data...</p>
          ) : setupData ? (
            <div className="space-y-6">
              <div className="flex flex-col items-center gap-4">
                <div className="text-center">
                  <p className="font-semibold mb-2">Scan this QR code:</p>
                  <div className="p-3 bg-gray-50 rounded-xl border">
                    <img
                      src={setupData.qr}
                      alt="2FA QR"
                      className="w-40 h-40"
                    />
                  </div>
                </div>
                <div className="text-center w-full">
                  <p className="font-semibold mb-2">
                    Or enter this secret key:
                  </p>
                  <code className="block bg-gray-100 text-sm p-3 rounded-md break-words text-gray-800 border">
                    {setupData.secret}
                  </code>
                </div>
              </div>

              <div>
                <p className="font-semibold mb-3 text-center">
                  Enter the 6-digit code from your Authenticator app:
                </p>
                <div className="flex justify-center gap-2">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <input
                      key={i}
                      type="text"
                      maxLength={1}
                      value={token[i] || ""}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, "");
                        let newOtp = token.split("");
                        newOtp[i] = val;
                        setToken(newOtp.join(""));
                        if (val && e.target.nextSibling) {
                          e.target.nextSibling.focus();
                        }
                      }}
                      onKeyDown={(e) => {
                        if (
                          e.key === "Backspace" &&
                          !token[i] &&
                          e.target.previousSibling
                        ) {
                          e.target.previousSibling.focus();
                        }
                      }}
                      className="w-10 h-12 text-center text-xl border-2 border-gray-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-500"
                    />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-center text-red-500">
              Failed to load setup data. Please try again.
            </p>
          )}
        </Modal>
      )}

      {/* ----------- BACKUP MODAL ----------- */}
      {showBackupModal && (
        <Modal
          show={showBackupModal}
          onClose={() => setShowBackupModal(false)}
          onConfirm={() => setShowBackupModal(false)}
          title="Backup Codes (Store these securely)"
        >
          <div className="text-center">
            <div className="bg-gray-100 p-4 rounded-md text-left max-h-48 overflow-y-auto">
              {setupData.backup_codes.map((code, index) => (
                <div key={index} className="font-mono text-sm py-1">
                  {code}
                </div>
              ))}
            </div>
            <button
              onClick={() => {
                const blob = new Blob([setupData.backup_codes.join("\n")], {
                  type: "text/plain",
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "backup_codes.txt";
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
            >
              Download Backup Codes
            </button>
          </div>
        </Modal>
      )}

      {/* ----------- DISABLE CONFIRM MODAL ----------- */}
      {showModal && (
        <Modal
          show={showModal}
          onClose={() => setShowModal(false)}
          onConfirm={handleConfirmDisable}
          title="Are you sure you want to disable Two-Factor Authentication?"
        />
      )}
    </>
  );
};

export default Profile;
